import { inventoryDB, remoteDB, salesDB } from '../integrations/pouch/DBConn';
import React from 'react';
import { DocumentPrefixes, SalesStatus, SalesTransactionResponseProps } from '../models';
import { getCurrentCart } from '../integrations/react_query/queryFn';
import { useOnlineStatus } from './useOnline';
import { getSessionInfo } from '../utils/getSessionInfo';
import { getSession } from '../utils/storage';
import {
	calculateOutstandingAmountWhenCreditOrPaid,
	calculateOverpaidAmount,
	calculateTotalAmount,
	calculateTotalAmountWithDiscount,
	calculateTotalAmountWithTax,
	calculateTotalDiscount,
	calculateTotalPayableAmount,
	calculateTotalPayments,
	calculateTotalTaxAmount,
} from '../utils/calcTotals';
import { UserResponseProps } from '../types';
import { generateUniqueID } from '../utils/generateUniqueId';
import { generateTransactionId } from '../utils/generateTransactionId';
import { handleCouchError } from '../utils/handleCouchError';
import { composeSingleInventoryLog, composeSaleInventoryId, classifyBulkWriteFailures } from '../utils/inventoryLog';
import { Tracking } from '../analytics/tracking';
import { EVENTS } from '../utils/events/index';
import { ELocalDBNames } from '../models/index';
import { addInfoBreadcrumb, captureException } from '../integrations/sentry/capture';

export const useCompleteSales = () => {
	const isOnline = useOnlineStatus();

	const transactionId = React.useRef<string>(generateTransactionId());

	// const worker = new Worker(new URL('.././workers/syncAndProcessSales.ts', import.meta.url));

	const completeTransaction = async () => {
		const { userInfo, userLocationObject, companyInfo } = getSessionInfo();
		const cartItems = await getCurrentCart();

		if (!cartItems) {
			Tracking.capture(EVENTS.SALES.FAILED, {
				reason: 'missing_cart_items',
				userId: userInfo?._id,
			});

			throw new Error('Cart item is required');
		}

		if (!userLocationObject) {
			Tracking.capture(EVENTS.SALES.FAILED, {
				reason: 'missing_user_location',
				userId: userInfo?._id,
			});

			throw new Error('Can not complete this sales, try again');
		}

		Tracking.capture(EVENTS.SALES.STARTED, {
			userId: userInfo?._id,
			merchant: userInfo?.merchantName,
			transactionId: transactionId.current,
			cartItemCount: cartItems.products.length,
			totalAmount: calculateTotalAmountWithTax(cartItems.products),
			payments: cartItems?.payments ?? [],
			isOnline,
		});

		const overrides = (getSession(`price-overrides-${cartItems._id}`) as Record<string, number>) || {};

		const productsWithAdjustedPrice = cartItems.products.map((cartItemsProduct) => {
			const override = overrides?.[cartItemsProduct.productId];
			const effectivePrice = typeof override !== 'undefined' ? Number(override) : cartItemsProduct.sellingPrice;
			const productForCalc = { ...cartItemsProduct, sellingPrice: effectivePrice } as any;
			return {
				...cartItemsProduct,
				adjustedPrice: typeof override !== 'undefined' ? Number(override) : null,
				tax: +calculateTotalTaxAmount([productForCalc]),
				totalAmount: +calculateTotalAmount([productForCalc]),
				totalAmountWithTax: +calculateTotalAmountWithTax([productForCalc]),
				totalDiscount: +calculateTotalDiscount([productForCalc]),
				payableAmount: +calculateTotalPayableAmount([productForCalc]),
			};
		});

		const outstandingAmount = +calculateOutstandingAmountWhenCreditOrPaid(
			calculateTotalAmountWithDiscount(productsWithAdjustedPrice),
			calculateTotalPayments(cartItems.payments ?? []),
			cartItems?.payments ?? []
		);

		const saleData: Omit<SalesTransactionResponseProps, 'payments'> & {
			totalAmount: number;
			totalAmountWithTax: number;
			amountPaid: number;
			outstandingAmount: number;
			discountTotal?: number;
			taxTotal?: number;
			overPaidAmount: number;
			isVoid: boolean;
			assignedLocation: UserResponseProps['assignedLocations'][0];
			payableAmount: number;
			taxPercentage: number;
		} = {
			...cartItems,
			// apply session overrides if present, include adjustedPrice, but keep original sellingPrice
			products: productsWithAdjustedPrice,
			note: null,
			salesPerson: {
				name: `${userInfo?.profile?.firstName} ${userInfo?.profile?.lastName}`,
				id: userInfo?._id as string,
			},
			status: SalesStatus.processing,
			createdAt: new Date().toISOString(),
			updatedAt: new Date().toISOString(),
			type: 'sales',
			_id: `sales-${String(transactionId.current)}`,
			transactionId: transactionId.current,
			assignedLocation: userLocationObject as UserResponseProps['assignedLocations'][0],
			payableAmount: +calculateTotalPayableAmount(productsWithAdjustedPrice),
			totalAmountWithTax: +calculateTotalAmountWithTax(productsWithAdjustedPrice),
			totalAmount: +calculateTotalAmount(productsWithAdjustedPrice),
			discountTotal: +calculateTotalDiscount(productsWithAdjustedPrice),
			invoiceId: generateUniqueID(DocumentPrefixes.INVOICE),
			salesId: generateUniqueID(DocumentPrefixes.SALES_ORDER),
			taxTotal: +calculateTotalTaxAmount(productsWithAdjustedPrice),
			// amountPaid: +calculateTotalPayments(cartItems?.payments || []),
			amountPaid:
				outstandingAmount > 0
					? +calculateTotalPayments(cartItems?.payments || []) - outstandingAmount
					: +calculateTotalPayments(cartItems?.payments || []),
			isVoid: false,
			outstandingAmount: +calculateOutstandingAmountWhenCreditOrPaid(
				calculateTotalAmountWithDiscount(productsWithAdjustedPrice),
				calculateTotalPayments(cartItems.payments ?? []),
				cartItems?.payments ?? []
			),
			// outstandingAmount: +calculateOutstandingAmountWithTax(
			// 	calculateTotalAmountWithDiscount(productsWithAdjustedPrice),
			// 	calculateTotalPayments(cartItems.payments ?? []),
			// ),
			overPaidAmount: +calculateOverpaidAmount(
				calculateTotalAmountWithDiscount(productsWithAdjustedPrice),
				calculateTotalPayments(cartItems.payments ?? [])
			),
			taxPercentage: companyInfo?.tax ?? 0,
		};

		const inventoryLogs = saleData?.products.map((item) => {
			const effectivePrice = (item as any).adjustedPrice ?? item.sellingPrice;
			return composeSingleInventoryLog({
				// Deterministic id: a second composition for the same sale line (client retry
				// or backend processSales) collides on _id instead of duplicating stock-out.
				overrideId: composeSaleInventoryId({
					saleDocId: saleData._id,
					productId: item.productId,
					variantId: item.variantId?.toString(),
				}),
				purchaseItem: {
					productId: item.productId,
					sellingPrice: effectivePrice,
					discount: item.discount ?? null,
					unitCost: item.unitCost,
					quantity: item.quantity,
					totalAmount: item.quantity * (effectivePrice || 1),
				},
				assignedLocation: userLocationObject as UserResponseProps['assignedLocations'][0],
				productName: item.productName,
				refCartId: item.refCartId,
				userFullName: saleData?.cashier?.name,
				userIdentity: saleData?.cashier?.id,
				userInfo: null,
				transactionStatus: 'sales',
				transactionType: 'stockOut',
				variantId: item.variantId?.toString(),
				referenceTransactionNo: saleData?.salesId,
				referenceTransactionId: saleData._id,
				note: saleData?.note ?? cartItems?.payments?.map((c) => c?.note)?.join(',') ?? '',
				sourceCreatedDate: saleData?.createdAt,
				referenceInvoiceId: saleData?.invoiceId,
				customer: saleData?.customer,
			});
		});

		try {
			const bulkResults = await inventoryDB().bulkDocs(inventoryLogs);
			const { conflicts: bulkConflicts, errors: bulkErrors } = classifyBulkWriteFailures(bulkResults);
			bulkConflicts.forEach((conflict) => {
				// Expected post-D1: deterministic _id already written for this sale line.
				addInfoBreadcrumb(`idempotent skip: ${'id' in conflict ? conflict.id : 'unknown'}`, {
					transactionId: saleData.transactionId,
					total: inventoryLogs.length,
				});
			});
			if (bulkErrors.length > 0) {
				captureException(new Error('bulkDocs partial failure'), {
					fn: 'completeSale.bulkDocs',
					args: { failures: bulkErrors, total: inventoryLogs.length },
				});
			}
			await salesDB().put(saleData);

			Tracking.capture(EVENTS.SALES.CREATED, {
				userId: userInfo?._id,
				transactionId: saleData.transactionId,
				salesId: saleData.salesId,
				merchant: userInfo?.merchantName,
				totalAmount: saleData.totalAmount,
				amountPaid: saleData.amountPaid,
				payableAmount: saleData.payableAmount,
				productCount: saleData.products.length,
				taxTotal: saleData.taxTotal,
				discountTotal: saleData.discountTotal,
				isOnline,
			});

			await inventoryDB()
				.replicate.to(remoteDB(userInfo?.merchantName as string), { retry: true })
				.on('complete', async (info) => {
					if (info.status === 'complete') {
						Tracking.capture(EVENTS.REPLICATION.completed(ELocalDBNames.inventory), {
							userId: userInfo?._id,
							transactionId: saleData.transactionId,
							salesId: saleData.salesId,
							replicatedAt: new Date().toISOString(),
							productCount: saleData.products.length,
						});
						// worker.postMessage({ userInfo, status: 'process-now', salesPayload: saleData });
					}
				})
				.on('error', (err: any) => {
					captureException(err, {
						fn: 'useCompleteSales.inventory.replicate.error',
						args: { transactionId: saleData.transactionId },
					});
					Tracking.capture(EVENTS.REPLICATION.FAILED(ELocalDBNames.inventory), {
						userId: userInfo?._id,
						transactionId: saleData.transactionId,
						salesId: saleData.salesId,
						error: err?.message,
					});
					handleCouchError(err);
				});

			await salesDB()
				.replicate.to(remoteDB(userInfo?.merchantName as string), { retry: true })
				.on('complete', async (info) => {
					if (info.status === 'complete') {
						Tracking.capture(EVENTS.REPLICATION.completed(ELocalDBNames.sales), {
							userId: userInfo?._id,
							transactionId: saleData.transactionId,
							salesId: saleData.salesId,
							replicatedAt: new Date().toISOString(),
						});
						// worker.postMessage({ userInfo, status: 'process-now', salesPayload: saleData });
					}
				})
				.on('error', (err: any) => {
					captureException(err, {
						fn: 'useCompleteSales.sales.replicate.error',
						args: { transactionId: saleData.transactionId },
					});
					Tracking.capture(EVENTS.REPLICATION.FAILED(ELocalDBNames.inventory), {
						userId: userInfo?._id,
						transactionId: saleData.transactionId,
						salesId: saleData.salesId,
						error: err?.message,
					});
					handleCouchError(err);
				});
		} catch (err: any) {
			Tracking.capture(EVENTS.SALES.FAILED, {
				error: err?.message,
				userId: userInfo?._id,
				transactionId: transactionId.current,
			});
			captureException(err, {
				fn: 'useCompleteSales.completeTransaction',
				args: { transactionId: transactionId.current },
			});

			throw err;
		} finally {
		}
	};

	return {
		completeTransaction,
		transactionId: transactionId.current,
	};
};
